/**
 * User type definitions
 * Extends PocketBase AuthModel with application-specific fields
 */

import type { AuthModel } from 'pocketbase'
import type { ProfileRole } from './agency'

// Re-export ProfileRole for convenience (the canonical role type)
export type UserRole = ProfileRole

// Profile object that may be expanded from a relation
export type UserProfile = {
  id?: string
  role?: UserRole
  agency_id?: string
  permissions?: string[]
}

/**
 * AppUser - Application user type that extends PocketBase AuthModel
 * Use this instead of `user as any` to get proper type safety
 */
export type AppUser = AuthModel & {
  // Agency assignment
  agency_id?: string

  // Role and permissions
  role?: UserRole
  is_super_admin?: boolean // Deprecated: use role === 'superadmin' instead

  // Team hierarchy
  lead_agent_id?: string // User's supervisor (relation to users)
  is_lead_agent?: boolean // True if user supervises others

  // Profile relation (may be expanded)
  profile?: UserProfile

  // PocketBase expand object
  expand?: {
    profile?: UserProfile
  }

  // Display fields
  name?: string
  first_name?: string
  last_name?: string
  avatar?: string
  email?: string

  // Settings
  notification_preferences?: {
    email?: boolean
    push?: boolean
    in_app?: boolean
  }
}

/**
 * Helper to safely get agency ID from user object
 */
export function getAgencyId(user: AppUser | null): string | null {
  if (!user) return null
  return user.agency_id || null
}

/**
 * Helper to check if user is super admin
 */
export function isSuperAdmin(user: AppUser | null): boolean {
  return getUserRole(user) === 'superadmin'
}

/**
 * Helper to get user role from various locations
 */
export function getUserRole(user: AppUser | null): UserRole | undefined {
  if (!user) return undefined
  return user.role || user.profile?.role || user.expand?.profile?.role || undefined
}

/**
 * Helper to check if user is a lead agent (supervisor)
 */
export function isLeadAgent(user: AppUser | null): boolean {
  return user?.is_lead_agent === true
}

/**
 * Helper to get user's lead agent ID (supervisor)
 */
export function getLeadAgentId(user: AppUser | null): string | null {
  if (!user) return null
  return user.lead_agent_id || null
}
